/**
 * §4.8, per person: **everything this person can reach.**
 *
 * Grouped by space and then by node, because "what can Sarah get to" is a
 * question about places rather than about capability strings, and the shape of
 * the answer should match the shape of the tree.
 *
 * Rows where no grant was found anywhere are omitted by the API — that is the
 * entire rest of the tree, and §4.6 already answers it with denied. What
 * remains is every (capability, node) pair some grant spoke to, allow or deny,
 * which is exactly the set worth reading. The page says so rather than leaving
 * the omission to be inferred from a short list.
 */

import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import { usePersonAccess, useSpaces } from '../../api/queries.ts';
import type { PersonAccessEntry } from '../../api/types.ts';
import { CAPABILITY_OPTIONS } from '../../components/capabilities.ts';
import {
  GrantSite,
  PrecedenceChips,
  PrecedenceLegend,
  type SubjectNamer,
  VerdictBadge,
} from '../../components/decision.tsx';
import { Badge, RoleBadge, StatusBadge } from '../../components/ui/badge.tsx';
import { LinkButton } from '../../components/ui/button.tsx';
import { Alert, EmptyState, Loading, PageError } from '../../components/ui/feedback.tsx';
import { Select } from '../../components/ui/field.tsx';
import { Card, CardHeader, Mono, PageHeader } from '../../components/ui/layout.tsx';
import { Table, TBody, TD, TH, THead, TR } from '../../components/ui/table.tsx';

interface NodeGroup {
  nodeId: string;
  title: string;
  entries: PersonAccessEntry[];
}

interface SpaceGroup {
  spaceId: string;
  spaceName: string;
  spaceKind: string | null;
  nodes: NodeGroup[];
}

function group(entries: PersonAccessEntry[]): SpaceGroup[] {
  const spaces = new Map<string, SpaceGroup>();

  for (const entry of entries) {
    const spaceId = entry.space?.id ?? entry.node.spaceId ?? 'unknown';
    let space = spaces.get(spaceId);
    if (!space) {
      space = {
        spaceId,
        spaceName: entry.space?.name ?? 'Space',
        spaceKind: entry.space?.kind ?? null,
        nodes: [],
      };
      spaces.set(spaceId, space);
    }

    let node = space.nodes.find((candidate) => candidate.nodeId === entry.node.id);
    if (!node) {
      node = { nodeId: entry.node.id, title: entry.node.title ?? entry.node.id, entries: [] };
      space.nodes.push(node);
    }
    node.entries.push(entry);
  }

  return [...spaces.values()];
}

export function PersonAccessPage() {
  const { personId } = useParams({ from: '/_authed/schema/access/people/$personId' });
  const search = useSearch({ from: '/_authed/schema/access/people/$personId' });
  const navigate = useNavigate();

  const access = usePersonAccess(personId, search.capability, search.spaceId);
  const spaces = useSpaces();

  const nameOf: SubjectNamer = useMemo(() => {
    const spaceNames = new Map((spaces.data?.spaces ?? []).map((space) => [space.id, space.name]));
    const nodeNames = new Map<string, string>();
    for (const entry of access.data?.entries ?? []) {
      if (entry.node.title) nodeNames.set(entry.node.id, entry.node.title);
      if (entry.space) spaceNames.set(entry.space.id, entry.space.name);
    }
    return (type, id) => (type === 'space' ? spaceNames.get(id) : nodeNames.get(id));
  }, [spaces.data, access.data]);

  const groups = useMemo(() => group(access.data?.entries ?? []), [access.data]);

  if (access.isPending) return <Loading />;
  if (access.isError) {
    return (
      <PageError
        error={access.error}
        title="What this person can reach"
        back={{ href: `/people/${personId}`, label: 'Back to their record' }}
      />
    );
  }

  const { person, entries } = access.data;
  const allowedCount = entries.filter((entry) => entry.decision.allowed).length;

  const setSearch = (patch: Record<string, string | undefined>) =>
    void navigate({
      to: '/schema/access/people/$personId',
      params: { personId },
      search: { ...search, ...patch },
      replace: true,
    });

  const spaceOptions = [
    { value: 'all', label: 'Every space' },
    ...(spaces.data?.spaces ?? []).map((space) => ({ value: space.id, label: space.name })),
  ];

  const capabilityOptions = [{ value: 'all', label: 'Every capability' }, ...CAPABILITY_OPTIONS];

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link
            to="/admin/people/$personId"
            params={{ personId }}
            className="text-tertiary no-underline"
          >
            {person.name}
          </Link>
        }
        title="What this person can reach"
        description={`${allowedCount} allowed of ${entries.length} (capability, node) pair(s) that some grant spoke to.`}
        actions={
          <LinkButton variant="secondary" href={`/people/${personId}`}>
            Their record
          </LinkButton>
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-medium text-primary">{person.name}</span>
          <RoleBadge role={person.role} />
          <StatusBadge status={person.status} />
          <span className="text-sm text-tertiary">{person.email}</span>
        </div>
      </Card>

      {person.status === 'deactivated' ? (
        <Alert tone="warning" title="Deactivated — denied everywhere">
          Everything below resolves to denied whatever the grants say. The resolver's actor step
          requires <span className="font-mono">status = 'active'</span>, which empties the result
          before a single grant is considered. That predicate is the half of offboarding that could
          not be done by deleting rows, because role-based reach has no rows to delete.
        </Alert>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Card>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Select
              label="Capability"
              className="flex-1"
              options={capabilityOptions}
              selectedKey={search.capability ?? 'all'}
              onSelectionChange={(value) =>
                setSearch({ capability: value === 'all' ? undefined : value })
              }
            />
            <Select
              label="Space"
              className="flex-1"
              options={spaceOptions}
              selectedKey={search.spaceId ?? 'all'}
              onSelectionChange={(value) =>
                setSearch({ spaceId: value === 'all' ? undefined : value })
              }
            />
          </div>
          <p className="mt-3 text-sm text-tertiary">
            Unfiltered, this evaluates all seventeen capabilities against every live node. That is
            fine at this scale and is the first thing to bound as the tree grows.
          </p>
        </Card>

        <Card>
          <h2 className="mb-2 text-sm font-semibold text-primary">Precedence</h2>
          <PrecedenceLegend />
        </Card>
      </div>

      {groups.length === 0 ? (
        <Card padded={false}>
          <EmptyState
            title="No grant anywhere speaks to this person"
            description="Not an empty tree — every (capability, node) pair resolved to rule 4, so nothing is listed. This is what a person with no grants correctly looks like."
          />
        </Card>
      ) : (
        groups.map((spaceGroup) => (
          <Card key={spaceGroup.spaceId} padded={false}>
            <CardHeader
              title={
                <span className="flex flex-wrap items-center gap-2">
                  <Link
                    to="/spaces/$spaceId"
                    params={{ spaceId: spaceGroup.spaceId }}
                    className="no-underline hover:underline"
                  >
                    {spaceGroup.spaceName}
                  </Link>
                  {spaceGroup.spaceKind ? (
                    <Badge tone="outline">{spaceGroup.spaceKind}</Badge>
                  ) : null}
                </span>
              }
              description={`${spaceGroup.nodes.length} node(s) with a grant speaking to them.`}
            />
            {spaceGroup.nodes.map((nodeGroup) => (
              <div key={nodeGroup.nodeId} className="border-b border-secondary last:border-b-0">
                <div className="flex flex-wrap items-center gap-2 bg-secondary px-4 py-2">
                  <Link
                    to="/nodes/$nodeId"
                    params={{ nodeId: nodeGroup.nodeId }}
                    className="text-sm font-medium text-primary no-underline hover:underline"
                  >
                    {nodeGroup.title}
                  </Link>
                  <Link
                    to="/schema/access/nodes/$nodeId"
                    params={{ nodeId: nodeGroup.nodeId }}
                    className="ml-auto text-xs link"
                  >
                    Who else can reach it
                  </Link>
                </div>
                <Table>
                  <THead>
                    <TR>
                      <TH>Capability</TH>
                      <TH>Verdict</TH>
                      <TH>Deciding grant sits on</TH>
                      <TH>Precedence</TH>
                      <TH>Why</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {nodeGroup.entries.map((entry) => (
                      <TR
                        key={`${entry.capability}:${entry.node.id}`}
                        className="hover:bg-secondary"
                      >
                        <TD>
                          <Badge tone="outline" mono>
                            {entry.capability}
                          </Badge>
                        </TD>
                        <TD>
                          <VerdictBadge decision={entry.decision} />
                        </TD>
                        <TD>
                          <GrantSite decision={entry.decision} nameOf={nameOf} />
                        </TD>
                        <TD>
                          <PrecedenceChips decision={entry.decision} />
                        </TD>
                        <TD className="max-w-sm text-xs">
                          {entry.explanation}
                          {entry.decision.grantId ? (
                            <Mono className="mt-1 block">{entry.decision.grantId}</Mono>
                          ) : null}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </div>
            ))}
          </Card>
        ))
      )}
    </>
  );
}
